import { useCallback, useMemo, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { finalizeEvent, generateSecretKey } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

import { useCurrentUser } from './useCurrentUser';
import { useLocalStorage } from './useLocalStorage';
import { useToast } from './useToast';

import { ANON_TAG, COMMUNITY_TAG, buildThreads, type Thread } from '@/lib/community';

const LOCAL_POSTS_KEY = 'calmcycle:my-posts';
const FEED_QUERY_KEY = ['nostr', 'community', COMMUNITY_TAG];

/** Fetch the community feed: questions and replies tagged #calmcycle. */
export function useCommunityFeed() {
  const { nostr } = useNostr();
  const [localPosts] = useLocalStorage<NostrEvent[]>(LOCAL_POSTS_KEY, []);

  const query = useQuery({
    queryKey: FEED_QUERY_KEY,
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [1], '#t': [COMMUNITY_TAG], limit: 200 }],
        { signal: c.signal },
      );
      return events;
    },
    staleTime: 30_000,
  });

  const threads: Thread[] = useMemo(() => {
    const remote = query.data ?? [];
    return buildThreads([...remote, ...localPosts]);
  }, [query.data, localPosts]);

  return { threads, isLoading: query.isLoading, isError: query.isError };
}

interface PostInput {
  content: string;
  anonymous: boolean;
  /** Event id of the question being replied to, if any. */
  parentId?: string;
}

/** Publish a question or reply, publicly (logged-in signer) or anonymously (throwaway key). */
export function useCommunityPost() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocalPosts] = useLocalStorage<NostrEvent[]>(LOCAL_POSTS_KEY, []);
  const [isPending, setIsPending] = useState(false);

  const post = useCallback(
    async ({ content, anonymous, parentId }: PostInput): Promise<NostrEvent> => {
      const tags: string[][] = [['t', COMMUNITY_TAG]];
      if (parentId) tags.push(['e', parentId, '', 'reply']);

      setIsPending(true);
      try {
        let event: NostrEvent;

        if (anonymous) {
          // Sign with a fresh throwaway key so the post can't be linked back.
          const secretKey = generateSecretKey();
          const signed = finalizeEvent(
            {
              kind: 1,
              content,
              tags: [...tags, ['t', ANON_TAG]],
              created_at: Math.floor(Date.now() / 1000),
            },
            secretKey,
          );
          event = signed as NostrEvent;
        } else {
          if (!user) {
            throw new Error('Log in to post publicly, or switch to anonymous.');
          }
          event = await user.signer.signEvent({
            kind: 1,
            content,
            tags,
            created_at: Math.floor(Date.now() / 1000),
          });
        }

        await nostr.event(event, { signal: AbortSignal.timeout(8000) });

        // Persist locally so the post appears instantly and survives reloads.
        setLocalPosts((prev) => (prev.some((e) => e.id === event.id) ? prev : [event, ...prev]));
        queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEY });

        return event;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to publish';
        toast({
          title: 'Could not publish your post',
          description: message,
          variant: 'destructive',
        });
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    [nostr, user, toast, queryClient, setLocalPosts],
  );

  return { post, isPending, canPostPublicly: Boolean(user) };
}
